import React, { useMemo, useState } from "react"

import {
  Activity,
  AlertTriangle,
  Calculator,
  Edit,
  Plus,
  Settings,
  Trash2,
  Truck,
} from "lucide-react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import IntegrationsReference from "@/components/collections/IntegrationsReference"
import MonitoringReference from "@/components/collections/MonitoringReference"
import { TableEmptyRow } from "@/components/shared/EmptyState"
import { LoadingState } from "@/components/shared/LoadingState"
import { PageHeader } from "@/components/shared/PageHeader"
import { Button } from "@/components/ui/button"
import { StatusPill } from "@/components/ui/status-pill"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  useCollectionsConfig,
  useCreateVehicleType,
  useDeleteVehicleType,
  useUpdateVehicleType,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { VehicleType } from "@/types/collections"
import {
  EMPTY_VEHICLE_TYPE_FORM,
  type VehicleTypeFormData,
  vehicleTypeFormFrom,
} from "@/lib/configForm"

import CostReference from "./config/CostReference"
import GlobalConfigTab from "./config/GlobalConfigTab"
import VehicleTypeDialog from "./config/VehicleTypeDialog"
import { useGlobalConfigForm } from "./config/useGlobalConfigForm"

type ConfigTab = "vehicle-types" | "global-config" | "cost-calculation" | "monitoring"

const ConfigContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ConfigTab>("vehicle-types")
  const [vehicleTypeDialogOpen, setVehicleTypeDialogOpen] = useState(false)
  const [editingVehicleType, setEditingVehicleType] = useState<VehicleType | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [vehicleTypeToDelete, setVehicleTypeToDelete] = useState<VehicleType | null>(null)
  const [vehicleTypeForm, setVehicleTypeForm] = useState<VehicleTypeFormData>(
    EMPTY_VEHICLE_TYPE_FORM,
  )

  const {
    data: vehicleTypesData,
    isLoading: vehicleTypesLoading,
    error: vehicleTypesError,
  } = useVehicleTypes()
  const { data: configData, isLoading: configLoading, error: configError } = useCollectionsConfig()

  const vehicleTypes = vehicleTypesData?.data || []
  const config = configData?.data

  const { configForm, setConfigForm, hasUnsavedChanges, handleSaveConfig, isSaving } =
    useGlobalConfigForm(config)

  const createVehicleType = useCreateVehicleType()
  const updateVehicleType = useUpdateVehicleType()
  const deleteVehicleType = useDeleteVehicleType()

  const calculatedChargingCostPerMile = useMemo(() => {
    const onSiteRate = configForm.on_site_charging_cost_per_kwh ?? 0
    if (vehicleTypeForm.miles_per_kwh > 0 && onSiteRate >= 0) {
      return onSiteRate / vehicleTypeForm.miles_per_kwh
    }
    return 0
  }, [vehicleTypeForm.miles_per_kwh, configForm.on_site_charging_cost_per_kwh])

  const handleCreateVehicleType = () => {
    setEditingVehicleType(null)
    setVehicleTypeForm({ ...EMPTY_VEHICLE_TYPE_FORM })
    setVehicleTypeDialogOpen(true)
  }

  const handleEditVehicleType = (vehicleType: VehicleType) => {
    setEditingVehicleType(vehicleType)
    setVehicleTypeForm(vehicleTypeFormFrom(vehicleType))
    setVehicleTypeDialogOpen(true)
  }

  const handleSaveVehicleType = async () => {
    if (!vehicleTypeForm.name.trim()) {
      toast.error("Validation Error", {
        description: "Vehicle type name is required",
      })
      return
    }

    if (vehicleTypeForm.monthly_lease_cost < 0) {
      toast.error("Validation Error", {
        description: "Monthly lease cost cannot be negative",
      })
      return
    }

    if (vehicleTypeForm.miles_per_kwh <= 0) {
      toast.error("Validation Error", {
        description: "Miles per kWh must be greater than 0",
      })
      return
    }

    if (vehicleTypeForm.labour_hourly_rate < 0) {
      toast.error("Validation Error", {
        description: "Labour hourly rate cannot be negative",
      })
      return
    }

    if (vehicleTypeForm.range_miles <= 0) {
      toast.error("Validation Error", {
        description: "Range (miles) must be greater than 0",
      })
      return
    }

    // The same bounds the columns check, so a bad value is named here rather than by Postgres.
    const { height_m, gross_weight_kg } = vehicleTypeForm
    if (height_m != null && (height_m <= 0 || height_m >= 6)) {
      toast.error("Validation Error", {
        description: "Height must be between 0 and 6 m",
      })
      return
    }

    if (gross_weight_kg != null && (gross_weight_kg <= 0 || !Number.isInteger(gross_weight_kg))) {
      toast.error("Validation Error", {
        description: "Gross weight must be a whole number of kg above 0",
      })
      return
    }

    try {
      if (editingVehicleType) {
        await updateVehicleType.mutateAsync({
          id: editingVehicleType.id,
          data: vehicleTypeForm,
        })
        toast.success("Success", {
          description: "Vehicle type updated successfully",
        })
      } else {
        await createVehicleType.mutateAsync(vehicleTypeForm)
        toast.success("Success", {
          description: "Vehicle type created successfully",
        })
      }
      setVehicleTypeDialogOpen(false)
    } catch (error) {
      toast.error("Error", {
        description: error instanceof Error ? error.message : "Failed to save vehicle type",
      })
    }
  }

  const handleDeleteVehicleType = (vehicleType: VehicleType) => {
    setVehicleTypeToDelete(vehicleType)
    setDeleteConfirmOpen(true)
  }

  const confirmDeleteVehicleType = async () => {
    if (!vehicleTypeToDelete) return

    try {
      await deleteVehicleType.mutateAsync(vehicleTypeToDelete.id)
      toast.success("Success", {
        description: "Vehicle type deleted successfully",
      })
      setDeleteConfirmOpen(false)
      setVehicleTypeToDelete(null)
    } catch (error) {
      toast.error("Error", {
        description: error instanceof Error ? error.message : "Failed to delete vehicle type",
      })
    }
  }

  if (vehicleTypesLoading || configLoading) {
    return <LoadingState className="flex-1 bg-palette-jungleGreen000" />
  }

  if (vehicleTypesError || configError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-palette-jungleGreen000">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-palette-jungleGreen900 mb-2">
              Error Loading Configuration
            </h2>
            <p className="text-palette-jungleGreen500 max-w-md">
              {vehicleTypesError?.message ||
                configError?.message ||
                "Failed to load configuration data"}
            </p>
            <p className="text-xs text-palette-jungleGreen400 mt-2">
              Check browser console for details
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-palette-jungleGreen000 min-h-0">
      <PageHeader
        title="Collections Configuration"
        subtitle="Configure vehicle types, costs, and driver wage rates for route cost estimation"
      />

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="px-6 py-4 flex-shrink-0">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ConfigTab)}>
            <TabsList className="bg-palette-jungleGreen100">
              <TabsTrigger value="vehicle-types" className="gap-2">
                <Truck className="w-4 h-4" />
                Vehicle Types
              </TabsTrigger>
              <TabsTrigger value="global-config" className="gap-2">
                <Settings className="w-4 h-4" />
                Global Config
              </TabsTrigger>
              <TabsTrigger value="cost-calculation" className="gap-2">
                <Calculator className="w-4 h-4" />
                Cost & Load Factor
              </TabsTrigger>
              <TabsTrigger value="monitoring" className="gap-2">
                <Activity className="w-4 h-4" />
                Monitoring
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 overflow-auto px-6 pb-6">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ConfigTab)}>
            <TabsContent value="vehicle-types" className="mt-0 space-y-4">
              <div className="bg-white rounded-lg border border-palette-jungleGreen200 shadow-sm">
                <div className="px-6 py-4 border-b border-palette-jungleGreen200">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-semibold text-palette-jungleGreen900">
                      Vehicle Types
                    </h2>
                    <Button onClick={handleCreateVehicleType} size="sm" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Add Vehicle Type
                    </Button>
                  </div>
                  <p className="text-sm text-palette-jungleGreen500">
                    Configure cost parameters for vehicle types used in route planning. These
                    vehicle types are also available when creating routes.
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Monthly Lease</TableHead>
                        <TableHead>Labour £/hr</TableHead>
                        <TableHead>Miles/kWh</TableHead>
                        <TableHead>Charging Cost/Mile</TableHead>
                        <TableHead>Range (mi)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vehicleTypes.length === 0 ? (
                        <TableEmptyRow colSpan={8}>
                          No vehicle types configured. Click "Add Vehicle Type" to get started.
                        </TableEmptyRow>
                      ) : (
                        vehicleTypes.map((vt) => (
                          <TableRow key={vt.id}>
                            <TableCell className="font-medium">
                              {vt.display_name || vt.name}
                            </TableCell>
                            <TableCell>£{vt.monthly_lease_cost.toFixed(2)}</TableCell>
                            <TableCell>£{(vt.labour_hourly_rate ?? 0).toFixed(2)}</TableCell>
                            <TableCell>{vt.miles_per_kwh.toFixed(2)}</TableCell>
                            <TableCell>
                              {config && vt.miles_per_kwh > 0
                                ? `£${((config.on_site_charging_cost_per_kwh ?? 0) / vt.miles_per_kwh).toFixed(4)}`
                                : "—"}
                            </TableCell>
                            <TableCell>{vt.range_miles}</TableCell>
                            <TableCell>
                              <StatusPill
                                tone={vt.is_active ? "success" : "neutral"}
                                label={vt.is_active ? "Active" : "Inactive"}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditVehicleType(vt)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteVehicleType(vt)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="global-config" className="mt-0 space-y-4">
              <GlobalConfigTab
                configForm={configForm}
                setConfigForm={setConfigForm}
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={handleSaveConfig}
                isSaving={isSaving}
              />
            </TabsContent>

            <TabsContent value="cost-calculation" className="mt-0 space-y-4">
              <CostReference
                vehicleTypes={vehicleTypes}
                config={config}
                configForm={configForm}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            </TabsContent>

            <TabsContent value="monitoring" className="mt-0 space-y-4">
              <MonitoringReference />
              <IntegrationsReference />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <VehicleTypeDialog
        open={vehicleTypeDialogOpen}
        onOpenChange={setVehicleTypeDialogOpen}
        isEditing={!!editingVehicleType}
        form={vehicleTypeForm}
        setForm={setVehicleTypeForm}
        chargingCostPerMile={calculatedChargingCostPerMile}
        onSave={handleSaveVehicleType}
        isSaving={createVehicleType.isPending || updateVehicleType.isPending}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "
              {vehicleTypeToDelete?.display_name || vehicleTypeToDelete?.name}"? This action cannot
              be undone. Note: If this vehicle type is used in existing routes, those routes will
              need to be updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteVehicleType}
              variant="destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ConfigContent
